import { stripeKeys } from './../../assets/data/stripe/keys';
import { Component, Input, OnInit, Output } from '@angular/core';
import { EventEmitter } from '@angular/core';
import { InAppBrowser } from '@ionic-native/in-app-browser';
import { TranslateService } from '@ngx-translate/core';
import { LoadingController, ToastController } from 'ionic-angular';
import { EventProvider } from '../../providers/event/event';
import { SessionProvider } from '../../providers/session/session';
import { NavigationSystem, PaymentService, Product, ProductType } from '../../providers/payment.service';

declare let Stripe;

@Component({
    selector: 'payment-component',
    templateUrl: 'payment.html'
})
export class PaymentComponent implements OnInit {
    stripePublish = Stripe(stripeKeys.live);

    @Input() user: User;
    @Input() product: Product;
    @Output() onSuccess = new EventEmitter<boolean>();

    // UI
    cardElement: any;
    activePaymentMethod: PaymentMethods = PaymentMethods.NONE;
    couponCode: string = "";

    constructor(
        private loadingCtrl: LoadingController,
        private eventService: EventProvider,
        private inAppBrowser: InAppBrowser,
        private toast: ToastController,
        private paymentService: PaymentService,
        private translate: TranslateService,
        private sessionService: SessionProvider
    ) { }

    ngOnInit() { }

    setPaymentMethod(pm: PaymentMethods) {
        let loader = this.loadingCtrl.create();
        loader.present();
        this.activePaymentMethod = pm;
        setTimeout(() => {
            switch (pm) {
                case PaymentMethods.CREDIT_CARD: this.setupStripeCredit(); break;
                case PaymentMethods.PAYPAL: this.openPaypalExternal(); break;
            };
            loader.dismiss();
        }, 2000);
    }

    setupStripeCredit() {
        let elements = this.stripePublish.elements();
        var style = {
            base: {
                fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
                color: '#32325d',
                lineHeight: '24px',
                fontSmoothing: 'antialiased',
                fontSize: '16px',
                '::placeholder': {
                    color: '#aab7c4'
                }
            },
            invalid: {
                color: '#fa755a',
                iconColor: '#fa755a'
            }
        };

        this.cardElement = elements.create('card',
            { hidePostalCode: true, style: style });
        this.cardElement.mount('#card-element');

        this.cardElement.addEventListener('change', event => {
            var displayError = document.getElementById('card-errors');
            if (event.error) {
                displayError.textContent = event.error.message;
            } else {
                displayError.textContent = '';
            }
        });
    }

    /**
     * Non-redirectable method
     * Instant emit on successful payment
     */
    payCreditCard() {
        let loader = this.loadingCtrl.create({});
        loader.present();
        const currency = this.product.currency.toLowerCase();
        let params = {
            amount: this.product.price * 100,        //amount is in cents
            currency: currency,
            paymentMethodType: "card"
        };
        let path = "pay?type=" + params.paymentMethodType + "&amount=" + params.amount + "&currency=" + params.currency;
        this.eventService.makeIntent(path).then((paymentClientIntent) => {
            this.confirmCardPayment(paymentClientIntent).then(() => {
                this.showToast(this.translate.instant('PAYMENT_SUCCESS'));
                this.onSuccess.emit(true);
                loader.dismiss();
            }).catch(err => {
                this.showToast(this.translate.instant('PAYMENT_FAILURE'));
                this.onSuccess.emit(false);
                loader.dismiss();
            });
        }).catch(err => alert(err));
    }
    confirmCardPayment(clientSecret) {
        return new Promise<any>(async (resolve, reject) => {
            const { error: stripeError, paymentIntent } = await this.stripePublish.confirmCardPayment(
                clientSecret,
                {
                    payment_method: {
                        card: this.cardElement,
                        billing_details: {
                            name: this.user.name,
                        },
                    },
                }
            );

            if (stripeError) {
                console.log(stripeError.message);
                alert(stripeError.message);
                // reenable the form.
                reject(null);
                return;
            }
            resolve(true);
        });
    }

    /**
      * redirectable function, requires savePaymentAttempt
      */
    async payGiropay() {
        let loader = this.loadingCtrl.create({});
        loader.present();
        let params = {
            amount: this.product.price * 100,        //amount is in cents
            currency: "eur",
            paymentMethodType: "giropay"
        };
        if ((params.amount + "").includes('.')) {
            params.amount = Number((params.amount + "").split('.')[0]);
        }
        let path = `pay?type=${params.paymentMethodType}&amount=${params.amount}&currency=${params.currency}`;
        this.eventService.makeIntent(path).then((paymentClientIntent) => {
            this.confirmGiropay(paymentClientIntent);
            loader.dismiss();
        }).catch(err => {
            this.showToast(this.translate.instant('PAYMENT_FAILURE'));
            loader.dismiss();
        });
    }
    confirmGiropay(clientIntent) {
        return new Promise<any>((resolve, reject) => {
            this.sessionService.getToken(false, false).then(async token => {
                this.paymentService.savePaymentAttempt({
                    navigationSystem: NavigationSystem.STACK,
                    // redirectSuccessModule: "QRCodePage",
                    // redirectFailureModule: "EventTabsPage",
                    token: token,
                    product: this.product,
                    method: "giropay",
                    clientIntent: clientIntent
                });
                this.stripePublish.confirmGiropayPayment(clientIntent,
                    {
                        payment_method: {
                            billing_details: {
                                name: this.user.name
                            }
                        },
                        return_url: window.location.href,
                    }
                );
            });
        });
    }

    /**
     * redirectable function, requires savePaymentAttempt
     */
    paySofort() {
        let loader = this.loadingCtrl.create({});
        loader.present();

        let params = {
            amount: this.product.price * 100,        //amount is in cents
            currency: "eur",
            paymentMethodType: "sofort"
        };
        
        if ((params.amount + "").includes('.')) {
            params.amount = Number((params.amount + "").split('.')[0]);
        }
        let path = `pay?type=${params.paymentMethodType}&amount=${params.amount}&currency=${params.currency}`;
        this.eventService.makeIntent(path).then((paymentClientIntent) => {
            this.confirmSofort(paymentClientIntent);
            loader.dismiss();
        }).catch(err => {
            let message = this.translate.instant('PAYMENT_FAILURE');
            this.showToast(message + " " + err);
            loader.dismiss();
        });
    }
    confirmSofort(clientIntent) {
        return new Promise<any>(async (resolve, reject) => {
            this.sessionService.getToken(false, false).then(async (token) => {
                this.paymentService.savePaymentAttempt({
                    navigationSystem: NavigationSystem.STACK,
                    // redirectSuccessModule: "QRCodePage",
                    // redirectFailureModule: "EventTabsPage",
                    token: token,
                    product: this.product,
                    method: "sofort",
                    clientIntent: clientIntent
                });
                const { error: stripeError, paymentIntent } = await this.stripePublish.confirmSofortPayment(
                    clientIntent,
                    {
                        payment_method: {
                            sofort: {
                                country: 'DE',
                            },
                            billing_details: {
                                name: this.user.name,
                                email: this.user.email
                            }
                        },
                        return_url: window.location.href,
                    }
                );
            });
        });
    }

    /**
     *  @TODO currently is usable only for EVENT_TICKET product type,
     *  need a refactoring on angular side 
     */
    openPaypalExternal() {
        this.sessionService.getToken(false, false).then(token => {
            let url = "https://spooorts.de/angulartheme/paypal/" + this.product.eventId + "/" + token + "?amount=" + this.product.price + "&currency=" + this.product.currency;
            let browser = this.inAppBrowser.create(url, '_blank', 'location=no');
            browser.show();
            browser.on('exit').subscribe(() => {
                this.eventService.isUserRegistered(this.product.eventId).then(isRegistered => {
                    if (isRegistered) {
                        this.onSuccess.emit(true);
                    }
                });
            });
        });
    }

    redeemCoupon($event) {
        if (this.couponCode.length <= 0 || !$event.target.innerHTML.includes('label')) return;

        this.sessionService.getToken(false, false).then((token) => {
            this.eventService.redeemCoupon(token, this.product.price, this.product.currency, this.couponCode).then((result) => {
                if (!result) {
                    this.showToast(this.translate.instant('COUPON_NULL_VALUE_CASE'));
                } else if (result > 0) {
                    this.showToast(this.translate.instant('COUPON_POSITIVE_VALUE_CASE'), true);
                    this.eventService.savePayment(this.product.eventId).then(() => {
                        this.onSuccess.emit(true);
                    }).catch((err) => console.error(err));
                } else {
                    this.showToast(this.translate.instant('COUPON_NEGATIVE_VALUE_CASE'), true);
                    this.product.price = result;
                }
            });
        });
    }

    showToast(translation, extra: boolean = false) {
        let message = extra
            ? this.translate.instant(translation, { amount: this.product.price, currency: this.product.currency })
            : this.translate.instant(translation);

        this.toast.create({
            message: message,
            duration: 3000,
            position: 'top'
        }).present();
    }

    public get paymentMethod(): typeof PaymentMethods {
        return PaymentMethods;
    }

    public get productType(): typeof ProductType {
        return ProductType;
    }
}

export interface User {
    name: string;
    email: string;
}
export enum PaymentMethods {
    NONE,
    PAYPAL,
    SOFORT,
    GIROPAY,
    CREDIT_CARD,
    COUPON
}


// WEBPACK FOOTER //
// /src/components/payment/payment.ts